import { memo, useEffect, useState } from "react"
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, Menu, MenuItem, Paper, Skeleton, SpeedDial, SpeedDialAction, Stack, TextField, Typography, Drawer, FormControl, InputLabel, Select, OutlinedInput, Checkbox, ListItemText, Divider, DialogContentText, List, ListItem, Popover, Breadcrumbs, Tooltip, Tab, Tabs, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Add, Cancel, Menu as MenuIcon, Edit, Search, FilterList, Clear, CloudUpload, NoteAlt, Movie, InsertDriveFile, Description, StickyNote2, VideoLibrary, NavigateNext, Info, School, Collections, Settings as SettingsIcon, CheckCircleOutline, Close, Public, Unpublished } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { createNewCollectionResource, Resource, ResourceStatus, ResourceType } from "../../../../models/resource";
import CreateResourceDialog from "./create-resource-dialog";
import EditResourceDialog from "./edit-resource-dialog";
import { Collection } from "../../../../models/collection";
import ResourceCard from "./resources-card";
import LoadingSkeleton from "../../components/loading-skeleton";
import { useSnackbar } from "../../../../components/snackbar";
import { useDomainActions } from "../../../../hooks/useDomainActions";
import { Hub } from "../../../../models/hub";
import isHubActivated from "../../../../utils/is-hub-activated";
import BackdropLoader from "../../../../components/backdrop-loader";
import sanitizeString from "../../../../utils/sanitize-name";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";


interface CollectionFilters {
    search: string;
    statuses: ResourceStatus[];
    types: ResourceType[];
}

interface ResourcesSectionProps {
    classroom_id: string;
    collection_id: string;
    collection: Collection;
    hub: Hub;
}

function ResourcesSection({ classroom_id, collection_id, collection, hub }: ResourcesSectionProps) {
    const { createResource } = useClassroomActions();

    const [resource, setResource] = useState<Resource>(createNewCollectionResource());

    const [filteredResources, setFilteredResources] = useState<Resource[] | null>(null);

    const [updatingResource, setUpdatingResource] = useState(-1);

    const [dialogOpen, setDialogOpen] = useState({
        create: false,
        edit: false,
    });

    const [uploadPercent, setUploadPercent] = useState<number | null>(null);
        
    const [fabMenuAnchor, setFabMenuAnchor] = useState<null | HTMLElement>(null);

    const [searchOpen, setSearchOpen] = useState(false);
    const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
    const [filterAnchorPosition, setFilterAnchorPosition] = useState({ top: 0, left: 0 });

    const [filters, setFilters] = useState<CollectionFilters>({
        search: '',
        statuses: [],
        types: []
    });

    const [deleting, setDeleting] = useState(false);

    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);


    const { showMessage } = useSnackbar();

    
    const actions = [
        { 
            icon: <StickyNote2 />, 
            name: 'Note',
            onClick: () => handleNewResourcePopOverClick("type", ResourceType.NOTE),
            color: '#ff9800'
        },
        { 
            icon: <VideoLibrary />, 
            name: 'Media',
            onClick: () => handleNewResourcePopOverClick("type", ResourceType.MEDIA),
            color: '#e91e63'
        },
        { 
            icon: <Description />, 
            name: 'Document',
            onClick: () => handleNewResourcePopOverClick("type", ResourceType.FILE),
            color: '#2196f3'
        },
    ];

    const clearFilters = () => {
        setFilters({
            search: '',
            statuses: [],
            types: []
        });
        setFilterAnchor(null);
    };

    const hasActiveFilters = filters.statuses.length > 0 || filters.types.length > 0;

    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setFilterAnchorPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX
        });
        setFilterAnchor(event.currentTarget);
    };

     const handleFilterChange = (field: keyof CollectionFilters, value: any) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleStatusChange = (status: ResourceStatus) => {
        setFilters((prev) => ({
            ...prev,
            statuses: prev.statuses.includes(status)
                ? prev.statuses.filter(s => s !== status)
                : [...prev.statuses, status]
        }));
    };

     
    const openDialog = (key: string) => {
        setDialogOpen((prev) => ({ ...prev, [key]: true }));
    };
  

    const handleTypeChange = (type: ResourceType) => {
        setFilters((prev) => ({
            ...prev,
            types: prev.types.includes(type)
                ? prev.types.filter(t => t !== type)
                : [...prev.types, type]
        }));
    };

     const handleNewResourcePopOverClick = (key: keyof Resource, value: ResourceType) => {
        if (!isHubActivated(hub)) {
            showMessage('Hub is not activated', 1);
            return;
        }
        
        const newRes = {
            ...createNewCollectionResource(),
            type: value
        };
        setResource(newRes);
        
        openDialog("create");
    };

    const handleRightClick = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
        });
    };

    const handlePaste = async () => {
        try {
            setUpdatingResource(0);
            const text = await navigator.clipboard.readText();
            const resource: Resource = JSON.parse(text);

            if (resource.classroom_id === Number(classroom_id) && resource.collection_id === Number(collection_id)) {
                showMessage('cannot paste in the same collection', 1);
                return;
            }
            
            const newResource: Resource = {
                ...createNewCollectionResource(),
                title: resource.title,
                description: resource.description,
                classroom_id: Number(classroom_id),
                collection_id: Number(collection_id),
                type: resource.type,
                status: ResourceStatus.DRAFT,
                url: resource.url
            };

            await createResource(
                Number(classroom_id),
                Number(collection_id),
                newResource,
                (percent) => {
                    setUploadPercent(percent)
                }
            );
            
            setUploadPercent(null);
            setUpdatingResource(-1);

        } catch (error) {
            showMessage('an error occured', 1);
            console.error('Failed to paste or parse resource:', error);
        }
    }
 
    useEffect(() => {
        if (!collection?.resources) return;

        const filtered = Object.values(collection.resources || {})
            .sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime())
            .filter((resource) => {
                const matchesSearch =
                resource.title.toLowerCase().includes(filters.search.toLowerCase())

                const matchesStatus =
                filters.statuses.length === 0 || filters.statuses.includes(resource.status);

                const matchesType =
                filters.types.length === 0 || filters.types.includes(resource.type);

                return matchesSearch && matchesStatus && matchesType;
            });
        setFilteredResources(filtered);
    }, [collection, filters])
    
    if (!filteredResources) {
        return <LoadingSkeleton/>
    }

    
    return (
        <Box onContextMenu={handleRightClick} sx={{ marginX: 'auto', maxWidth: 'lg', minHeight: '100vh' }} >
            <Box sx={{ padding: 1, display: 'flex', alignItems: "center", gap: 0.5 }}>
                <Box 
                    sx={{
                        flexGrow: 1,
                        animation: 'slideDown 0.2s ease-out'
                    }}
                >
                    <TextField
                        fullWidth
                        placeholder="Search resources..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        autoFocus
                        InputProps={{
                             sx: {
                                backgroundColor: "white",
                                borderRadius: 2,
                            },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="primary" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Stack direction="row" spacing={1}>
                                        {filters.search && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleFilterChange('search', '')}
                                            >
                                                <Clear />
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchOpen(false)}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Stack>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'primary.main',
                                },
                            },
                            display: searchOpen ? "flex" : "none",
                        }}
                    />
                </Box> 

                <Stack direction="row" spacing={0.5} height="fit-content">
                    <IconButton
                        onClick={() => setSearchOpen(!searchOpen)}
                        sx={{
                            backgroundColor: searchOpen ? 'action.selected' : 'transparent',
                            '&:hover': { backgroundColor: 'action.hover' }
                        }}
                    >
                        <Search />
                    </IconButton>
                
                    <IconButton
                        onClick={handleFilterClick}
                        sx={{
                            backgroundColor: hasActiveFilters ? 'primary.main' : 'transparent',
                            color: hasActiveFilters ? 'white' : 'inherit',
                            '&:hover': { 
                            backgroundColor: hasActiveFilters ? 'primary.dark' : 'action.hover' 
                            },
                        }}
                    >
                        <FilterList />
                    </IconButton>
                </Stack>

                <Menu
                    anchorEl={filterAnchor}
                    open={Boolean(filterAnchor)}
                    onClose={() => setFilterAnchor(null)}
                    anchorReference="anchorPosition"
                    anchorPosition={
                        filterAnchor
                            ? { top: filterAnchorPosition.top, left: filterAnchorPosition.left }
                            : undefined
                    }
                    PaperProps={{
                        sx: { 
                            minWidth: 300,
                            mt: 1,
                            borderRadius: 2,
                        }
                    }}
                >
                    <Box sx={{ p: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                            Filter Resources
                        </Typography>
                        
                        <Stack spacing={2} direction="column">
                            <Stack>
                                <Typography variant="body2" fontWeight="medium">
                                    Status
                                </Typography>
                                {[
                                    { value: ResourceStatus.DRAFT, label: 'Draft', color: 'warning.main' },
                                    { value: ResourceStatus.PUBLISHED, label: 'Published', color: 'success.main' },
                                    { value: ResourceStatus.ARCHIVED, label: 'Archived', color: 'error.main' }
                                    ].map((status) => (
                                    <Box
                                        key={status.value}
                                        onClick={() => handleStatusChange(status.value)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            p: 1,
                                            borderRadius: 1,
                                            '&:hover': { backgroundColor: 'grey.100' }
                                        }}
                                    >
                                        <Checkbox
                                            checked={filters.statuses.includes(status.value)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        />
                                        <Box sx={{ 
                                            width: 8, 
                                            height: 8, 
                                            bgcolor: status.color, 
                                            borderRadius: '50%',
                                            mr: 1
                                        }} />
                                        <Typography variant="body2">
                                            {status.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>

                            <Stack>
                                <Typography variant="body2" fontWeight="medium">
                                    Type
                                </Typography>
                                {[
                                    { value: ResourceType.NOTE, label: 'Note', icon: '📝' },
                                    { value: ResourceType.MEDIA, label: 'Media', icon: '🎬' },
                                    { value: ResourceType.FILE, label: 'File', icon: '📄' }
                                ].map((type) => (
                                    <Box
                                        key={type.value}
                                        onClick={() => handleTypeChange(type.value)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            p: 1,
                                            borderRadius: 1,
                                            '&:hover': { backgroundColor: 'grey.100' }
                                        }}
                                    >
                                        <Checkbox
                                            checked={filters.types.includes(type.value)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        />
                                        <Typography sx={{ mr: 1 }}>{type.icon}</Typography>
                                        <Typography variant="body2">
                                            {type.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                            
                            <Button 
                                variant="outlined" 
                                size="small" 
                                onClick={clearFilters}
                                fullWidth
                                disabled={!hasActiveFilters}
                            >
                                Clear
                            </Button>
                        </Stack>
                    </Box>
                </Menu>

            </Box>

            {/* Resources Grid or Empty States */}
            {filteredResources.length > 0 ? (
                <Grid container spacing={2} padding={2} sx={{ flexGrow: 1 }}>
                    {filteredResources.map((resource) => (
                        <Grid key={resource.id} size={{ xs: 12, md: 4 }}>
                            <ResourceCard 
                                classroom_id={Number(classroom_id)}
                                collection_id={Number(collection_id)}
                                resource={resource} 
                                setResource={setResource} 
                                openDialog={openDialog}
                                role="admin"
                                updatingResource={updatingResource}
                                setUpdatingResource={setUpdatingResource}
                                uploadPercent={uploadPercent}
                                hub={hub}
                                setDeleting={setDeleting}
                            />                  
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        minHeight: '400px',
                        textAlign: 'center',
                        px: 3,
                        py: 6
                    }}
                >
                    <Box
                        sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            backgroundColor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                        }}
                    >
                        <Collections sx={{ fontSize: 48, color: 'grey.400' }} />
                    </Box>

                    <Typography variant="h5" fontWeight="600" sx={{ mb: 1, color: 'grey.800' }}>
                        No resources yet
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 3, color: 'grey.600', maxWidth: 400 }}>
                        Get started by creating your first resource. You can add notes, media files, or documents to organize your content.
                    </Typography>
                    
                    
                </Box>
            )}

            <Menu
                open={contextMenu !== null}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem
                    onClick={async () => {
                        await handlePaste();
                        setContextMenu(null);
                    }}
                >
                    Paste
                </MenuItem>
            </Menu>


           <Box
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1000,
                }}
            >
                <Menu
                    anchorEl={fabMenuAnchor}
                    open={Boolean(fabMenuAnchor)}
                    onClose={() => setFabMenuAnchor(null)}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            minWidth: 160,
                            mb: 1,
                        }
                    }}
                >
                    {actions.map((action) => (
                        <MenuItem
                            key={action.name}
                            onClick={() => {
                                action.onClick();
                                setFabMenuAnchor(null);
                            }}
                            sx={{
                                py: 1.5,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    backgroundColor: action.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                }}
                            >
                                {action.icon}
                            </Box>
                            <Typography variant="body2" fontWeight={500}>
                                {action.name}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>

                <Button
                    variant="contained"
                    onClick={(e) => setFabMenuAnchor(e.currentTarget)}
                    disabled={uploadPercent !== null}
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        minWidth: 'unset',
                        backgroundColor: '#2563eb',
                        boxShadow: '0 4px 16px rgba(37, 99, 235, 0.3)',
                        '&:hover': {
                            backgroundColor: '#1d4ed8',
                            boxShadow: '0 6px 20px rgba(37, 99, 235, 0.4)',
                            transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                    
                >
                    <Add sx={{ fontSize: 24 }} />
                </Button>
            </Box>

            
            <CreateResourceDialog
                classroom_id={classroom_id!}
                collection_id={collection_id!}
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                setUploadPercent={setUploadPercent}
                role="admin"
                res={resource}
                setUpdatingResource={setUpdatingResource}
            />
            
            <EditResourceDialog
                res={resource}
                dialogOpen={dialogOpen}
                setDialogOpen={setDialogOpen}
                setUpdatingResource={setUpdatingResource}
                setUploadPercent={setUploadPercent}
            />

            {deleting && (<BackdropLoader/>)}
        </Box>
    )
}

export default memo(ResourcesSection);